import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { MarginPercentage } from '@plato/ui-legacy/components/list-views/margin-percentage';
import { PricePerUnitLabel } from '@plato/ui-legacy/components/list-views/price-per-unit-label';
import { DataText } from '@plato/ui-legacy/components/typography/data-text';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import { QuantityText } from '@plato/ui-legacy/components/typography/quantity-text';
import { useResolvedLocale } from '@plato/ui-legacy/lib/hooks/locale';
import type { ColumnDef } from '@plato/ui/components/data-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtendedSalesOrderItem } from '../../../server/types/grid-expanded-types.ts';
import { LineItemStatusBadge } from '../../statuses/components/line-item-status-badge.tsx';
import { ProductLinkCell } from '../components/product-link-cell.tsx';

export type TransactionDetailLineItemsColumnsOptions = {
    type?: SrSalesOrderType;
};

export function useTransactionDetailLineItemsColumns(options: TransactionDetailLineItemsColumnsOptions) {
    const { t } = useTranslation();
    const locale = useResolvedLocale();
    const { type = SrSalesOrderType.ORDER } = options;
    const { canReadGrossMargin, canReadLineItemRebate, canReadLineItemPurchasePrice } = useAppConfig();

    const transactionDetailLineItemsColumns: ColumnDef<string, ExtendedSalesOrderItem>[] = useMemo(
        () => [
            {
                accessorKey: 'product_name',
                header: t('table_header__product_name', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <ProductLinkCell
                        erpProductId={row.original.product_variant_id}
                        productName={row.original.product_name}
                    />
                ),
                size: 200,
            },
            {
                accessorKey: 'line_status',
                header: t('table_header__line_status', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => <LineItemStatusBadge status={row.original.status} type={type} />,
            },
            {
                accessorKey: 'quantity',
                header: t('table_header__quantity_ordered', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <QuantityText quantity={row.original.quantity} unit={row.original.quantity_unit_sales} />
                ),
            },
            {
                accessorKey: 'price_per_unit',
                header: t('table_header__price', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <>
                        <DataText>
                            <CurrencyOrEmpty decimals={2}>{row.original.sales_price}</CurrencyOrEmpty>
                        </DataText>
                        <PricePerUnitLabel
                            scaleFactor={row.original.price_scale_factor}
                            units={row.original.quantity_unit_sales}
                            locale={locale}
                        />
                    </>
                ),
            },
            {
                accessorKey: 'discount_percent',
                meta: {
                    enabled: canReadLineItemRebate,
                    aiChat: {
                        requires: ['canReadLineItemRebate'],
                    },
                },
                header: t('table_header__rebate', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) =>
                    typeof row.original.discount_percent === 'number' ? (
                        <DataText>
                            <PercentageOrEmpty>{row.original.discount_percent / 100}</PercentageOrEmpty>
                        </DataText>
                    ) : (
                        '-'
                    ),
            },
            {
                accessorKey: 'line_total_money',
                header: t('table_header__total', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }) => (
                    <DataText>
                        <CurrencyOrEmpty decimals={2}>{row.original.line_total}</CurrencyOrEmpty>
                    </DataText>
                ),
            },
            {
                accessorKey: 'order_line_gross_margin',
                meta: {
                    enabled: canReadGrossMargin,
                    aiChat: {
                        label: t('table_header__gross_margin', { ns: 'line-item-table' }),
                        requires: ['canReadGrossMargin'],
                    },
                },
                header: () => (
                    <MaskedContent>
                        {t('table_header__gross_margin', { ns: 'line-item-table' })}
                    </MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <CurrencyOrEmpty decimals={2}>{row.original.gross_margin}</CurrencyOrEmpty>
                        </DataText>
                    </MaskedContent>
                ),
            },
            {
                accessorKey: 'line_gross_margin_percentage',
                meta: {
                    enabled: canReadGrossMargin,
                    aiChat: {
                        label: t('table_header__gross_margin_percentage', { ns: 'line-item-table' }),
                        description: 'Gross margin as a percentage of the line total',
                        requires: ['canReadGrossMargin'],
                    },
                },
                header: () => (
                    <MaskedContent>
                        {t('table_header__gross_margin_percentage', { ns: 'line-item-table' })}
                    </MaskedContent>
                ),
                cell: ({ row }) => (
                    <MaskedContent>
                        <DataText>
                            <MarginPercentage
                                margin={row.original.gross_margin}
                                revenue={row.original.line_total}
                            />
                        </DataText>
                    </MaskedContent>
                ),
            },
            {
                accessorKey: 'encrypted_purchase_price',
                meta: {
                    enabled: canReadLineItemPurchasePrice,
                    aiChat: {
                        description:
                            "The ERP's coded cost token for the line, not a readable price. Empty when the ERP is unavailable",
                        requires: ['canReadLineItemPurchasePrice'],
                    },
                },
                header: t('table_header__encrypted_purchase_price', {
                    ns: 'line-item-table',
                }),
                cell: ({ row }: { row: { original: ExtendedSalesOrderItem } }) =>
                    row.original.encrypted_purchase_price,
            },
        ],
        [canReadGrossMargin, canReadLineItemPurchasePrice, canReadLineItemRebate, locale, t, type],
    );

    return transactionDetailLineItemsColumns;
}
